import type { FC } from "react"
import { useCallback, useRef } from "react"

import { AS400_RECONCILIATION_MESSAGE, AS400_VOID_RECONCILIATION_MESSAGE } from "../constants/invoiceAs400Sync"
import { INVOICE_AS400_SYNC_KIND_TO_LABEL } from "../constants/labels"
import { useConfirmAs400ManualReconciliation } from "../hooks/useConfirmAs400ManualReconciliation"

import { InvoiceCommentConfirmDialog } from "./InvoiceCommentConfirmDialog"

import { InvoiceAs400SyncKind } from "@/graphql/codegen/graphql"

interface InvoiceAs400ReconciliationDialogProps {
  invoiceId: string
  syncKind: InvoiceAs400SyncKind
  onClose: () => void
  onSuccess: () => void
  onFailure: () => void
}

export const InvoiceAs400ReconciliationDialog: FC<InvoiceAs400ReconciliationDialogProps> = ({
  invoiceId,
  syncKind,
  onClose,
  onSuccess,
  onFailure,
}) => {
  const { confirmAs400ManualReconciliation, isConfirming } = useConfirmAs400ManualReconciliation()

  const isInFlightRef = useRef(false)

  const handleConfirm = async (comment: string) => {
    if (isInFlightRef.current) return
    isInFlightRef.current = true
    try {
      const isSuccess = await confirmAs400ManualReconciliation({ invoiceId, syncKind, comment })
      if (isSuccess) onSuccess()
      else onFailure()
    } finally {
      isInFlightRef.current = false
    }
  }

  // A late onSuccess would close whichever kebab dialog the user opened next, so closing waits for the request.
  const handleClose = useCallback(() => {
    if (isInFlightRef.current || isConfirming) return
    onClose()
  }, [isConfirming, onClose])

  return (
    <InvoiceCommentConfirmDialog
      title='Confirm Manual AS400 Reconciliation'
      content={
        <>
          <span className='mb-2 block font-medium' data-testid='invoices.as400-reconciliation-dialog.failed-operation'>
            Failed operation: {INVOICE_AS400_SYNC_KIND_TO_LABEL[syncKind]}
          </span>
          <span>
            {syncKind === InvoiceAs400SyncKind.Void ? AS400_VOID_RECONCILIATION_MESSAGE : AS400_RECONCILIATION_MESSAGE}
          </span>
        </>
      }
      commentLabel='Comment'
      commentPlaceholder='Enter comment...'
      confirmLabel='Mark as Resolved'
      closeLabel='Cancel'
      isProcessing={isConfirming}
      onClose={handleClose}
      onConfirm={handleConfirm}
      testIdPrefix='invoices.as400-reconciliation-dialog'
    />
  )
}
